import type {InitOptions} from 'i18next';

/**
 * The two i18next settings that decide how a message is *read*, as opposed to
 * which messages exist. They live here because the app is not the only thing
 * that has to know them.
 *
 * `prefix`/`suffix` make interpolation single-braced, so the copy writes
 * `{count}` and i18next's own default `{{count}}` is never parsed — a message
 * written the default way renders its placeholder literally in the browser.
 * `pluralSeparator: '.'` makes a plural key a nested `{one, few, many, other}`
 * object rather than i18next's default sibling `key_one` keys, which is the
 * shape Phrase exports.
 *
 * **Jest can see neither of them.** The setup replaces i18next with a stripped
 * copy whose `t` returns the key, so every spec in the monorepo passes against
 * a placeholder that never interpolates and a plural that never picks a form.
 * The one spec that reads published copy —
 * `libs/dms/modules/orchestration/src/i18n/__tests__/countedMessages.spec.ts` —
 * builds its own i18next out of `jest.requireActual` and has to configure it
 * the same way, or it proves nothing about what ships. It imports this, so
 * "the same way" is a fact and not a promise: these four values transcribed
 * into that file let the app's config move with every test still green.
 */
export const MESSAGE_FORMAT_OPTIONS = {
  interpolation: {
    escapeValue: false,
    formatSeparator: ',',
    prefix: '{',
    suffix: '}',
  },
  pluralSeparator: '.',
} satisfies Pick<InitOptions, 'interpolation' | 'pluralSeparator'>;
